import type { CustomInfoType } from "$lib/models/base";
import { googleInfoTypes } from "./googleInfoTypes";

/**
 * The categories we show, and the colour each one gets.
 *
 * Membership of this map is what makes a category exist: `SensitiveDataTree.groupFindings` drops a
 * finding whose category is not defined here, with no log line, so a category without a colour is
 * a category nobody can see. Colours are ours because Google does not publish any.
 *
 * Deliberately wider than the current scan set. Every org keeps its stored detector list until
 * someone runs `redactive ingest sdp-info-types --sync`, so findings from detectors we no longer
 * request are still arriving and still need somewhere to land.
 */
const CATEGORY_COLOR: Record<string, string> = {
  AMERICAN_BANKERS_CUSIP_ID: "#FDE2E2",
  AUSTRALIA_DRIVERS_LICENSE_NUMBER: "#FEE2E2",
  AUSTRALIA_MEDICARE_NUMBER: "#F3E5F5",
  AUSTRALIA_PASSPORT: "#F3E5F5",
  AUSTRALIA_TAX_FILE_NUMBER: "#FEE2E2",
  CREDIT_CARD_DATA: "#FCA5A5",
  DEMOGRAPHIC_DATA: "#D1FAE5",
  DRIVERS_LICENSE_NUMBER: "#FFFDE7",
  EMAIL_ADDRESS: "#DBEAFE",
  FINANCIAL_ID: "#E8EAF6",
  GOVERNMENT_ID: "#FFF3E0",
  MEDICAL_DATA: "#FFEBEE",
  MEDICAL_ID: "#F8E1E7",
  PASSPORT: "#F3E5F5",
  PHONE_NUMBER: "#FDE68A",
  SECURITY_DATA: "#FFCDD2",
  STORAGE_SIGNED_POLICY_DOCUMENT: "#E1F5FE",
  TECHNICAL_ID: "#E0F2F1",
  US_ADOPTION_TAXPAYER_IDENTIFICATION_NUMBER: "#FDE2F6",
  US_BANK_ROUTING_MICR: "#E2FDE4",
  US_DEA_NUMBER: "#E2FDF9",
  US_DRIVERS_LICENSE_NUMBER: "#FDF4E2",
  US_EMPLOYER_IDENTIFICATION_NUMBER: "#E2F9FD",
  US_HEALTHCARE_NPI: "#E2F4FD",
  US_INDIVIDUAL_TAXPAYER_IDENTIFICATION_NUMBER: "#FDE2E8",
  US_MEDICARE_BENEFICIARY_ID_NUMBER: "#FDF2E2",
  US_PASSPORT: "#FDF2E8",
  US_PREPARER_TAXPAYER_IDENTIFICATION_NUMBER: "#E2FDF2",
  US_SOCIAL_SECURITY_NUMBER: "#FDE8E2",
  US_TOLLFREE_PHONE_NUMBER: "#E2F6FD",
  US_VEHICLE_IDENTIFICATION_NUMBER: "#FFCCBC",
  VEHICLE_IDENTIFICATION_NUMBER: "#FFAB91",
};

/**
 * Labels that override Google's `displayName`.
 *
 * Only for disambiguation, not for taste. These three sit in the tree directly alongside the
 * Australian and US categories promoted out of them, and Google's names -- "Passport number" next
 * to "Australia passport number" -- do not say which is which.
 */
const LABEL_OVERRIDE: Record<string, string> = {
  DRIVERS_LICENSE_NUMBER: "International driver's license number",
  GOVERNMENT_ID: "International government ID",
  PASSPORT: "International passport number",
};

export interface CategoryMeta {
  label: string;
  color?: string;
  description?: string;
  example?: string;
  isCustom?: boolean;
}

export const categoryMeta: Record<string, CategoryMeta> = Object.fromEntries(
  Object.entries(CATEGORY_COLOR).map(([name, color]) => [
    name,
    {
      label: LABEL_OVERRIDE[name] ?? googleInfoTypes[name]?.displayName ?? name,
      color,
      description: googleInfoTypes[name]?.description,
      example: googleInfoTypes[name]?.example,
    },
  ])
);

// Default color for custom infoTypes
export const CUSTOM_INFO_TYPE_COLOR = "#E8D5E8";

/** The two fields of a custom info type that name it as a category: key and label. */
export type NamedCustomInfoType = Pick<CustomInfoType, "name" | "displayName">;

/** A custom info type's category label: its display name, or its raw name when that is empty. */
export function customInfoTypeLabel(customType: NamedCustomInfoType): string {
  return customType.displayName || customType.name;
}

/**
 * Get combined category metadata including custom infoTypes from the organization.
 * Custom infoTypes get their displayName as the label.
 */
export function getCategoryMetaWithCustom(
  customInfoTypes: Pick<CustomInfoType, "name" | "displayName" | "enabled">[],
): Record<string, CategoryMeta> {
  const combined: Record<string, CategoryMeta> = { ...categoryMeta };

  for (const customType of customInfoTypes) {
    if (customType.enabled && customType.name) {
      combined[customType.name] = {
        label: customInfoTypeLabel(customType),
        color: CUSTOM_INFO_TYPE_COLOR,
        isCustom: true,
      };
    }
  }

  return combined;
}

/** Chip background for a category with no colour of its own, including the uncategorised bucket. */
export const UNCATEGORISED_COLOUR = "#E5E7EB";

/**
 * The chip background for a rolled-up info-type category, shared by the Info Types cell and the
 * row detail panel so the two agree. The uncategorised bucket has no `categoryMeta` entry, so it
 * falls through to the same neutral grey as any category without a colour. A custom info type's
 * category is keyed by its raw name, which is not in `categoryMeta`, so it carries `isCustom` and
 * gets {@link CUSTOM_INFO_TYPE_COLOR}, as on the Sensitive Data Detection page.
 */
export function categoryColour({ category, isCustom = false }: { category: string; isCustom?: boolean }): string {
  if (isCustom) return CUSTOM_INFO_TYPE_COLOR;
  return categoryMeta[category]?.color ?? UNCATEGORISED_COLOUR;
}
